import { Component, inject } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { SessionService } from '../../services/session.service';

/**
 * The single section nav for every signed-in role — the dashboard tabs and the
 * admin tabs in one bar (was two separate bars, app-admin-nav and
 * app-dashboard-nav, each visible only on its own pages).
 *
 * Each getter below mirrors the route guard for the tabs it controls, so a tab
 * is shown only where navigating to it actually succeeds. Keep them in step: a
 * looser getter means a dead tab, a stricter one hides a page the user may use.
 */
@Component({
    selector: 'app-admin-nav',
    imports: [RouterLink, RouterLinkActive],
    templateUrl: './admin-nav.component.html',
    styleUrl: './admin-nav.component.scss',
})
export class AdminNavComponent {
    private session = inject(SessionService);

    /** Account Details is reachable by anyone signed in — the page itself only
     *  checks isLoggedIn, and adapts for cloud customers. */
    get isLoggedIn(): boolean {
        return this.session.isLoggedIn();
    }

    /** Mirrors adminGuard. NOTE: a cloud session can carry isAdmin (legacy
     *  is_admin, or the FYES_FORCE_ADMIN dev flag), and the backend's
     *  fyesAdminRequired does not require isLocal either. */
    get isAdmin(): boolean {
        return this.session.isLoggedIn() && this.session.isAdmin();
    }

    /**
     * Mirrors dashboardGuard: `isLocal && canAccessOrders`. The isLocal half
     * matters — the dashboard acts on Fyes_AdminUsers identities (assignedTo,
     * resolvedBy), which a cloud session has no row for, so without it a cloud
     * admin would see Overview/Orders tabs that the guard then bounces.
     */
    get canAccessOrders(): boolean {
        return this.session.isLoggedIn() && this.session.isLocal() && this.session.canAccessOrders();
    }

    /**
     * The file-portal user: a local account with neither admin rights nor orders
     * access. Sales (canAccessOrders) is deliberately excluded — they get the
     * dashboard and nothing else.
     */
    get isFilePortalUser(): boolean {
        return this.session.isLoggedIn() && this.session.isLocal() && !this.session.canAccessOrders();
    }

    /** Cloud gateway customer: their own gateway list, never the admin tabs. */
    get isCloudCustomer(): boolean {
        return this.session.isLoggedIn() && !this.session.isLocal() && !this.session.isAdmin();
    }
}
